import { getEnabledTabs } from '../utils/utils';
import {
  ALLOWED_LOCALES,
  asDateString,
  Location,
  Locations,
  MAX_PAX_AMOUNT,
  PaxAmount,
  TravelClass,
  TravelDates,
  TravelType,
  WidgetTab,
  REGEX_PROMO_CODE,
} from '../common';
import { DeepPartial, isIn, isPresent, sanitize } from '../utils';
import { BookingState, AmSelectionState, initialSelectionState, SelectionState } from './state';
import { locationsAreSame } from './stateUtils';
import { storage, Storage } from './storage';
import { Middleware, Store } from './types';
import { Profile } from '../interfaces';
import { isEmpty } from 'lodash';

export interface PersistState extends SelectionState {}

const mapFromStorage = (selection: SelectionState): Partial<BookingState> => {
  if (!selection || Array.isArray(selection.locations)) {
    return { selection };
  }
  const locations = [selection.locations];
  return { selection: { ...selection, locations } };
};

export const needToUpdate = (prev: BookingState, next: BookingState): boolean => prev.selection !== next.selection;

const mapToStorageAm = ({ amState: { amSelection } }: Partial<BookingState>): AmSelectionState => amSelection;

const mapFromStorageAm = (amSelection: AmSelectionState): Partial<AmSelectionState> => amSelection;

const needToUpdateAm = (prev: BookingState, next: BookingState): boolean =>
  prev.amState.amSelection !== next.amState.amSelection;

// == Storage mapping ===========================================================

export const mapToStorage = ({ selection }: Partial<BookingState>): PersistState => {
  const { locations = [{}], ...rest } = selection;
  return {
    ...rest,
    locations: locations.map((location) => {
      return Object.keys(location).reduce((acc: Locations, key: string) => {
        if (isPresent(location[key])) {
          const { locationCode } = location[key];
          acc[key] = { locationCode };
        }
        return acc;
      }, {});
    }),
  };
};

// == Constants ===========================================================

const storageKeySuffix = '';
const storageKeySuffixAm = 'AM';

export const persistMiddleware =
  (storage: Storage, key: string): Middleware<BookingState> =>
  (store: Store<BookingState>) =>
  (next: (action) => Promise<void>) =>
  (action: () => void) => {
    const prev: BookingState = store.getState();
    return Promise.resolve(next(action)).then((result: any) => {
      const post: BookingState = store.getState();
      if (needToUpdate(prev, post)) {
        storage.set(key + storageKeySuffix, mapToStorage(post));
      }
      if (needToUpdateAm(prev, post)) {
        storage.set(key + storageKeySuffixAm, mapToStorageAm(post));
      }
      return result;
    });
  };

export const readInitialState = (storage: Storage, key: string): Partial<BookingState> => {
  const data = storage.get<SelectionState>(key + storageKeySuffix);
  return isPresent(data) ? mapFromStorage(data) : data;
};
export const readInitialAmState = (storage: Storage, key: string): Partial<AmSelectionState> => {
  const data = storage.get<AmSelectionState>(key + storageKeySuffixAm);
  return isPresent(data) ? mapFromStorageAm(data) : data;
};
export const writeBookingState = (key: string, state: Partial<BookingState>): void =>
  storage.session.set(key, mapToStorage(state));

// == Selections sanitization =============================

export const sanitizeTravelType = (travelType: TravelType): TravelType => {
  if (isPresent(travelType)) {
    return isIn(travelType, [TravelType.ONE_WAY, TravelType.ROUND_TRIP, TravelType.MULTI_CITY])
      ? travelType
      : initialSelectionState.travelType;
  }
  return travelType;
};

export const sanitizeTravelClass = (travelClass: TravelClass): TravelClass => {
  if (isPresent(travelClass)) {
    return isIn(travelClass, [
      TravelClass.BUSINESS,
      TravelClass.PREMIUM_ECONOMY,
      TravelClass.ECONOMY,
      TravelClass.MIXED,
    ])
      ? travelClass
      : initialSelectionState.travelClass;
  }
  return travelClass;
};

export const sanitizeTravelDate = (travelDate: string): string => {
  const dateIsValid = !isNaN(Date.parse(travelDate));
  if (isPresent(travelDate) && dateIsValid) {
    return asDateString(new Date(travelDate)) === travelDate ? travelDate : undefined;
  }
  return undefined;
};

export const sanitizeTravelDates = (
  travelDates: Partial<TravelDates>,
  travelType?: TravelType
): Partial<TravelDates> => {
  if (isPresent(travelDates)) {
    return sanitize({
      departureDate: sanitizeTravelDate(travelDates?.departureDate),
      returnDate: travelType === TravelType.ONE_WAY ? undefined : sanitizeTravelDate(travelDates?.returnDate),
      flexible: travelDates?.flexible,
    });
  }

  return travelDates;
};

export const sanitizePaxAmount = (paxAmount: Partial<PaxAmount>): Partial<PaxAmount> => {
  if (isPresent(paxAmount)) {
    const { adults = 1, c15s = 0, children = 0, infants = 0 } = paxAmount;
    const leadPaxCount: number = adults + c15s;
    const validTotalAmount: boolean =
      leadPaxCount > 0 && leadPaxCount + children <= MAX_PAX_AMOUNT && infants <= leadPaxCount;

    return validTotalAmount ? { adults, c15s, children, infants } : initialSelectionState.paxAmount;
  }

  return paxAmount;
};

export const sanitizePromoCode = (promoCode: string): string => {
  return REGEX_PROMO_CODE.test(promoCode) ? promoCode : null;
};

export const sanitizeLocationCode = (locationCode: string): string => {
  return isPresent(locationCode) && locationCode.length === 3 ? locationCode.toUpperCase() : undefined;
};
export const sanitizeLocation = (location: Partial<Location>): Partial<Location> =>
  location?.locationCode ? { locationCode: sanitizeLocationCode(location.locationCode) } : undefined;

export const sanitizeLocations = (
  locations: DeepPartial<Locations>[],
  prefillDestination = true,
  travelType: TravelType
): DeepPartial<Locations>[] => {
  let cleanedLocations = [];
  if (isPresent(locations)) {
    cleanedLocations = locations
      ?.map((location) => {
        const origin: Partial<Location> = sanitizeLocation((location as Partial<Locations>)?.origin);
        const destination: Partial<Location> = sanitizeLocation((location as Partial<Locations>)?.destination);
        const sanitizedLocations: Partial<Locations> = sanitize({ origin, destination }) as Partial<Locations>;

        // If the locations given with updates are duplicates e.g. origin=HEL & destination=HEL
        // As it is an invalid state an we don't want to pass that forward, we simply
        // remove the destination as a sanitizing measure
        // compact widget prefill disabled to avoid the initial flickering
        if (!prefillDestination || locationsAreSame(sanitizedLocations)) {
          return {
            origin,
          };
        }
        return sanitizedLocations;
      })
      .filter((ond) => !isEmpty(ond));
  }

  // Locations should always be an array with two objects when multi city, otherwise after refresh origin will be undefined since it's cleaned
  if (travelType === TravelType.MULTI_CITY && cleanedLocations?.length === 1) {
    cleanedLocations.push({});
  }

  return cleanedLocations;
};

export const sanitizeLocale = (locale: string): string => {
  return isIn(locale, ALLOWED_LOCALES) ? locale : undefined; // TODO: some fallback instead of undefined?
};

export const sanitizeActivatedTab = (
  tab: WidgetTab,
  locale: string,
  profile: Profile,
  enableAward: boolean,
  enableAwardIfLoggedIn,
  enableAurinkomatkat: boolean
): WidgetTab => {
  if (isPresent(tab)) {
    const enabledTabs = getEnabledTabs(locale, profile, enableAward, enableAwardIfLoggedIn, enableAurinkomatkat);
    return isIn(tab, enabledTabs) ? tab : initialSelectionState.activatedTab;
  }
  return tab;
};

export const sanitizeSelection = (
  selection: DeepPartial<SelectionState>,
  profile: DeepPartial<Profile>,
  enableAward: boolean,
  enableAwardIfLoggedIn: boolean,
  enableAurinkomatkat: boolean,
  prefillDestination: boolean
): DeepPartial<SelectionState> => {
  if (isPresent(selection)) {
    const {
      locale,
      locations,
      paxAmount,
      travelClass,
      travelType,
      travelDates,
      activatedTab,
      promoCode,
      directFlights,
    } = selection;

    const sanitizedSelection: DeepPartial<SelectionState> = {
      locale: sanitizeLocale(locale),
      locations: sanitizeLocations(locations as Partial<Locations>[], prefillDestination, selection.travelType),
      paxAmount: sanitizePaxAmount(paxAmount),
      travelClass: sanitizeTravelClass(travelClass),
      travelType: sanitizeTravelType(travelType),
      travelDates: sanitizeTravelDates(travelDates, travelType),
      activatedTab: sanitizeActivatedTab(
        activatedTab,
        locale,
        { isLoggedIn: profile?.isLoggedIn ?? false },
        enableAward,
        enableAwardIfLoggedIn,
        enableAurinkomatkat
      ),
      promoCode: sanitizePromoCode(promoCode),
      directFlights,
    };
    return sanitize(sanitizedSelection);
  }
  return {};
};
